import React from "react";
import "./attendance.css";
import { FaCalendarAlt, FaExclamationTriangle, FaSearch, FaStar, FaInfoCircle } from "react-icons/fa";

// ==================== HELPERS ====================
const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
    time: date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
};

const formatTime12Hour = (timeString) => {
  if (!timeString) return "—";
  if (timeString.includes("T") || timeString.includes("-")) {
    return formatDateTime(timeString).time;
  }
  const parts = timeString.split(":");
  if (parts.length < 2) return timeString;
  let hour = parseInt(parts[0], 10);
  const minute = parts[1];
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12;
  return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
};

const getArrivalDeviation = (checkInStr, expectedArrivalStr) => {
  if (!checkInStr || !expectedArrivalStr) return null;
  const checkIn = new Date(checkInStr);
  const expected = new Date(checkInStr);
  const [hours, minutes] = expectedArrivalStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = checkIn - expected;
  if (diffMs === 0) return null;
  const absMinutes = Math.abs(Math.floor(diffMs / 60000));
  return diffMs > 0 ? { minutes: absMinutes, type: "late" } : { minutes: absMinutes, type: "early" };
};

const getDepartureDeviation = (checkOutStr, expectedLeavingStr) => {
  if (!checkOutStr || !expectedLeavingStr) return null;
  const checkOut = new Date(checkOutStr);
  const expected = new Date(checkOutStr);
  const [hours, minutes] = expectedLeavingStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = checkOut - expected;
  if (diffMs === 0) return null;
  const absMinutes = Math.abs(Math.floor(diffMs / 60000));
  return diffMs > 0 ? { minutes: absMinutes, type: "extra" } : { minutes: absMinutes, type: "early" };
};

const formatDuration = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined) return "";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const getWorkingMinutes = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  return Math.floor((end - start) / (1000 * 60));
};

const getWorkingHoursDisplay = (checkIn, checkOut, totalPauseMinutes = 0) => {
  const totalMinutes = getWorkingMinutes(checkIn, checkOut);
  const actualMinutes = totalMinutes - (totalPauseMinutes || 0);
  if (actualMinutes <= 0) return "0m";
  const hours = Math.floor(actualMinutes / 60);
  const minutes = actualMinutes % 60;
  if (hours === 0 && minutes === 0) return "0m";
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const getExpectedDailyMinutes = (expectedArrival, expectedLeaving) => {
  if (!expectedArrival || !expectedLeaving) return 0;
  const [arrH, arrM] = expectedArrival.split(":").map(Number);
  const [levH, levM] = expectedLeaving.split(":").map(Number);
  return (levH * 60 + levM) - (arrH * 60 + arrM);
};

const formatHoursMins = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
};

const getArrivalLateMinutes = (checkInStr, expectedArrivalStr) => {
  if (!checkInStr || !expectedArrivalStr) return 0;
  const checkIn = new Date(checkInStr);
  const expected = new Date(checkInStr);
  const [hours, minutes] = expectedArrivalStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = checkIn - expected;
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const getEarlyDepartureMinutes = (checkOutStr, expectedLeavingStr) => {
  if (!checkOutStr || !expectedLeavingStr) return 0;
  const checkOut = new Date(checkOutStr);
  const expected = new Date(checkOutStr);
  const [hours, minutes] = expectedLeavingStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = expected - checkOut;
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const getLocalDateString = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ✅ Timezone-safe date parser (same as admin)
const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, m, d] = match;
  return {
    year: parseInt(y, 10),
    month: parseInt(m, 10),
    day: parseInt(d, 10),
    toDateObj: () => new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)),
    dateStr: `${y}-${m}-${d}`,
  };
};

const isOffDay = (dayOfWeek, role) => {
  const normalizedRole = String(role || '').toLowerCase();
  if (dayOfWeek === 0) return true;
  if (dayOfWeek === 6 && (normalizedRole === 'intern' || normalizedRole === 'student')) return true;
  return false;
};

// ==================== SPECIAL INTERN HELPERS ====================
// Rule: Sunday off • Work window 12:00 PM – 5:00 PM • Weekly minimum 15h

const INTERN_SPECIAL_WINDOW_START = 12 * 60;           // 12:00 PM in minutes
const INTERN_SPECIAL_WINDOW_END = 17 * 60;             // 5:00 PM in minutes
const INTERN_SPECIAL_DAILY_EXPECTED_MINUTES = 3 * 60;  // 3h (display only)
const INTERN_SPECIAL_WEEKLY_MIN_MINUTES = 15 * 60;     // 15h

// ✅ Special off day check (Sunday only)
const isSpecialOffDay = (dayOfWeek) => {
  return dayOfWeek === 0;
};

// ✅ Effective worked minutes inside the window (12 PM - 5 PM only)
const getSpecialWindowMinutes = (checkInStr, checkOutStr) => {
  if (!checkInStr || !checkOutStr) return 0;
  const checkIn = new Date(checkInStr);
  const checkOut = new Date(checkOutStr);

  const checkInMins = checkIn.getHours() * 60 + checkIn.getMinutes();
  const checkOutMins = checkOut.getHours() * 60 + checkOut.getMinutes();

  const startMins = Math.max(checkInMins, INTERN_SPECIAL_WINDOW_START);
  const endMins = Math.min(checkOutMins, INTERN_SPECIAL_WINDOW_END);

  return Math.max(0, endMins - startMins);
};

const getEmployeeRole = (records) => {
  if (!records || records.length === 0) {
    try {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        if (user.role) return String(user.role).toLowerCase();
      }
    } catch (e) {}
    return 'employee';
  }
  const firstRecord = records[0];
  if (firstRecord.role) {
    return String(firstRecord.role).toLowerCase();
  }
  return 'employee';
};

// ✅ NEW: Get user's rule_type from localStorage
const getUserRuleType = () => {
  try {
    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      return String(user.rule_type || "").toLowerCase();
    }
  } catch (e) {}
  return "";
};

const getOffDaysDescription = (role) => {
  const normalizedRole = String(role || "").toLowerCase().trim();
  if (normalizedRole === "intern_special") return "Sunday only (special)";
  if (normalizedRole === "intern" || normalizedRole === "student") return "Sundays & Saturdays";
  return "Sundays only";
};

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const AttendanceTable = ({ records = [], holidays = [], leaves = [], searchTerm = "", filterMonth = "", onSearchChange, onFilterChange }) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  let statsMonth, statsYear;
  
  if (filterMonth && filterMonth.includes('-')) {
    const [year, month] = filterMonth.split('-').map(Number);
    statsYear = year;
    statsMonth = month - 1;
  } else if (filterMonth) {
    statsMonth = parseInt(filterMonth) - 1;
    statsYear = currentYear;
  } else {
    statsMonth = currentMonth;
    statsYear = currentYear;
  }
  
  const totalDaysInMonth = new Date(statsYear, statsMonth + 1, 0).getDate();
  const employeeRole = getEmployeeRole(records);
  
  // ✅ NEW: Check if user is special intern
  const userRuleType = getUserRuleType();
  const isSpecialIntern = userRuleType === "intern_special";

  const isCurrentMonth = (statsMonth === currentMonth && statsYear === currentYear);
  const currentDay = isCurrentMonth ? now.getDate() : totalDaysInMonth;

  const holidayDates = new Set(holidays.map(h => h.holiday_date));
  // ✅ Timezone-safe leave dates
  const leaveDates = new Set(
    leaves.map(l => parseDateSafe(l.leave_date)).filter(Boolean).map(p => p.dateStr)
  );

  const availableYears = [];
  const currentYr = currentYear;
  let earliestYear = currentYr;
  if (records && records.length > 0) {
    records.forEach(r => {
      if (r.check_in) {
        const d = new Date(r.check_in);
        if (d.getFullYear() < earliestYear) earliestYear = d.getFullYear();
      }
    });
  }
  earliestYear = Math.min(earliestYear, currentYr - 1);
  for (let y = earliestYear; y <= currentYr; y++) {
    availableYears.push(y);
  }

  const monthlyRecords = records.filter(r => {
    if (!r.check_in) return false;
    const d = new Date(r.check_in);
    return d.getMonth() === statsMonth && d.getFullYear() === statsYear;
  });

  const hasData = monthlyRecords.length > 0;

  // ==================== STATS CALCULATION ====================
  let workingDaysPassed = 0;
  let totalWorkingDaysInMonth = 0;
  let allWorkingDaysPassed = [];
  let actualWorkingDays = 0;
  let absentDates = [];
  let leaveDatesList = [];
  let totalLeaves = 0;
  let actualDaysWorked = 0;
  let expectedDailyMinutes = 0;
  let totalExpectedMonthMinutes = 0;
  let currentExpectedMinutes = 0;
  let totalWorkedMinutes = 0;
  let totalPauseMinutesMonth = 0;
  let totalGrossMinutes = 0;
  let totalLateMinutes = 0;
  let holidayDaysPassed = 0;
  let totalHolidaysInMonth = 0;
  let totalEarlyDepartureMinutes = 0;
  let leaveDaysPassed = 0;
  let totalLeaveDaysInMonth = 0;
  let totalAbsentDays = 0;

  // Special intern specific
  let specialWeeklyData = {};
  let specialWeeklySummary = [];
  let specialExtraMinutes = 0;
  let specialShortfallMinutes = 0;
  let specialAbsentMinutes = 0;
  let specialLeaveMinutes = 0;
  let specialTotalWorkedMinutes = 0;

  if (hasData) {
    const lastDayToCheck = isCurrentMonth ? currentDay : totalDaysInMonth;
    
    workingDaysPassed = 0;
    allWorkingDaysPassed = [];
    holidayDaysPassed = 0;
    leaveDaysPassed = 0;
    
    // ✅ Use appropriate off-day function
    const offDayFn = isSpecialIntern ? isSpecialOffDay : (dow) => isOffDay(dow, employeeRole);
    
    for (let d = 1; d <= lastDayToCheck; d++) {
      const dateObj = new Date(statsYear, statsMonth, d);
      const dayOfWeek = dateObj.getDay();
      const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      
      if (!offDayFn(dayOfWeek)) {
        if (holidayDates.has(dateStr)) {
          holidayDaysPassed++;
        } else if (leaveDates.has(dateStr)) {
          leaveDaysPassed++;
        } else {
          workingDaysPassed++;
          allWorkingDaysPassed.push({
            day: d,
            date: dateStr,
            dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" })
          });
        }
      }
    }

    totalWorkingDaysInMonth = 0;
    totalHolidaysInMonth = 0;
    totalLeaveDaysInMonth = 0;
    
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dayOfWeek = new Date(statsYear, statsMonth, d).getDay();
      const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      
      if (!offDayFn(dayOfWeek)) {
        if (holidayDates.has(dateStr)) {
          totalHolidaysInMonth++;
        } else if (leaveDates.has(dateStr)) {
          totalLeaveDaysInMonth++;
        } else {
          totalWorkingDaysInMonth++;
        }
      }
    }

    const workingDaysSet = new Set(
      monthlyRecords
        .map(r => {
          if (!r.check_in) return null;
          return getLocalDateString(r.check_in);
        })
        .filter(Boolean)
    );
    
    actualWorkingDays = workingDaysSet.size;

    absentDates = allWorkingDaysPassed.filter(day => 
      !workingDaysSet.has(day.date) && !holidayDates.has(day.date) && !leaveDates.has(day.date)
    );
    
    // ✅ Build leave dates list (timezone-safe, current month only)
    leaveDatesList = leaves
      .map(l => parseDateSafe(l.leave_date))
      .filter(parsed => {
        if (!parsed) return false;
        if (parsed.year !== statsYear) return false;
        if (parsed.month !== statsMonth + 1) return false;
        const dateObj = parsed.toDateObj();
        return !holidayDates.has(parsed.dateStr) && !offDayFn(dateObj.getDay());
      })
      .map(parsed => ({
        day: parsed.day,
        date: parsed.dateStr,
        dayName: parsed.toDateObj().toLocaleDateString("en-US", { weekday: "short" }),
      }))
      .sort((a, b) => a.day - b.day);

    // ==================== SPECIAL INTERN CALCULATION ====================
    if (isSpecialIntern) {
      // Group attendance by date
      const attByDate = {};
      monthlyRecords.forEach((r) => {
        if (!r.check_in) return;
        const d = new Date(r.check_in);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        attByDate[dateStr] = r;
      });

      // Weekly tracking
      const weeklyData = {};
      let totalAbsentDaysCount = 0;
      const specialAbsentDates = [];
      let totalWorkedMin = 0;
      let workingDaysPassedCount = 0;

      for (let d = 1; d <= totalDaysInMonth; d++) {
        const dateObj = new Date(statsYear, statsMonth, d);
        const dayOfWeek = dateObj.getDay();
        const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

        // Monday week start
        const dayOffset = (dayOfWeek + 6) % 7;
        const weekStart = new Date(dateObj);
        weekStart.setDate(weekStart.getDate() - dayOffset);
        const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            weekLabel: `Week of ${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
            weekStartDate: weekKey,
            workedMinutes: 0,
            daysWorked: 0,
            expectedDays: 0,
          };
        }

        // Skip Sunday
        if (isSpecialOffDay(dayOfWeek)) continue;

        // Skip holidays
        if (holidayDates.has(dateStr)) continue;

        // Skip leaves
        if (leaveDates.has(dateStr)) continue;

        // Working day
        weeklyData[weekKey].expectedDays++;

        const isPast = d <= currentDay;
        const rec = attByDate[dateStr];

        if (isPast) {
          workingDaysPassedCount++;
          if (rec) {
            const windowMinutes = getSpecialWindowMinutes(rec.check_in, rec.check_out);
            if (windowMinutes > 0) {
              totalWorkedMin += windowMinutes;
              weeklyData[weekKey].workedMinutes += windowMinutes;
              weeklyData[weekKey].daysWorked++;
            } else {
              totalAbsentDaysCount++;
              specialAbsentDates.push({
                day: d,
                date: dateStr,
                dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
              });
            }
          } else {
            totalAbsentDaysCount++;
            specialAbsentDates.push({
              day: d,
              date: dateStr,
              dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
            });
          }
        }
      }

      // Weekly summary
      specialWeeklySummary = Object.values(weeklyData)
        .filter((w) => w.expectedDays > 0)
        .map((w) => {
          const shortfallMinutes = Math.max(0, INTERN_SPECIAL_WEEKLY_MIN_MINUTES - w.workedMinutes);
          const extraMinutes = Math.max(0, w.workedMinutes - INTERN_SPECIAL_WEEKLY_MIN_MINUTES);
          const meetsMinimum = w.workedMinutes >= INTERN_SPECIAL_WEEKLY_MIN_MINUTES;
          return {
            weekLabel: w.weekLabel,
            weekStartDate: w.weekStartDate,
            workedMinutes: w.workedMinutes,
            expectedMinutes: INTERN_SPECIAL_WEEKLY_MIN_MINUTES,
            daysWorked: w.daysWorked,
            expectedDays: w.expectedDays,
            meetsMinimum,
            shortfallMinutes,
            extraMinutes,
          };
        })
        .sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

      const weeksInMonth = specialWeeklySummary.length;
      const totalExpectedSpecial = weeksInMonth * INTERN_SPECIAL_WEEKLY_MIN_MINUTES;

      specialExtraMinutes = specialWeeklySummary.reduce((sum, w) => sum + w.extraMinutes, 0);
      specialShortfallMinutes = specialWeeklySummary.reduce((sum, w) => sum + w.shortfallMinutes, 0);
      specialAbsentMinutes = totalAbsentDaysCount * INTERN_SPECIAL_DAILY_EXPECTED_MINUTES;
      specialLeaveMinutes = totalLeaveDaysInMonth * INTERN_SPECIAL_DAILY_EXPECTED_MINUTES;
      specialTotalWorkedMinutes = totalWorkedMin;

      // Override values
      expectedDailyMinutes = INTERN_SPECIAL_DAILY_EXPECTED_MINUTES;
      totalExpectedMonthMinutes = totalExpectedSpecial;
      totalWorkedMinutes = totalWorkedMin;
      totalAbsentDays = totalAbsentDaysCount;
      absentDates = specialAbsentDates;
      totalPauseMinutesMonth = 0;
      totalLateMinutes = 0;
      totalEarlyDepartureMinutes = 0;
    } else {
      // Normal calculation
      totalLeaves = absentDates.length;
      actualDaysWorked = workingDaysPassed - totalLeaves;

      const firstWithExpected = monthlyRecords.find(r => r.expected_arrival && r.expected_leaving);
      expectedDailyMinutes = firstWithExpected
        ? getExpectedDailyMinutes(firstWithExpected.expected_arrival, firstWithExpected.expected_leaving)
        : 0;

      const totalWorkingDaysForExpected = totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0);
      totalExpectedMonthMinutes = totalWorkingDaysForExpected * expectedDailyMinutes;
      currentExpectedMinutes = actualDaysWorked * expectedDailyMinutes;

      totalWorkedMinutes = monthlyRecords.reduce((total, r) => {
        const grossMinutes = getWorkingMinutes(r.check_in, r.check_out);
        const pauseMinutes = r.total_pause_minutes || 0;
        return total + Math.max(0, grossMinutes - pauseMinutes);
      }, 0);

      totalPauseMinutesMonth = monthlyRecords.reduce((total, r) => total + (r.total_pause_minutes || 0), 0);
      totalGrossMinutes = monthlyRecords.reduce((total, r) => total + getWorkingMinutes(r.check_in, r.check_out), 0);

      totalLateMinutes = monthlyRecords.reduce((total, r) => {
        return total + getArrivalLateMinutes(r.check_in, r.expected_arrival);
      }, 0);

      totalEarlyDepartureMinutes = monthlyRecords.reduce((total, r) => {
        return total + getEarlyDepartureMinutes(r.check_out, r.expected_leaving);
      }, 0);
    }
  }

  const filteredRecords = records && records.length > 0
    ? records.filter((record) => {
        if (!record || !record.check_in) return false;
        const dateStr = formatDateTime(record.check_in).date.toLowerCase();
        const matchesSearch = dateStr.includes(searchTerm.toLowerCase());
        if (filterMonth) {
          const d = new Date(record.check_in);
          const recordMonth = d.getMonth() + 1;
          const recordYear = d.getFullYear();
          if (filterMonth.includes('-')) {
            const [fy, fm] = filterMonth.split('-').map(Number);
            return matchesSearch && recordMonth === fm && recordYear === fy;
          }
          return matchesSearch && recordMonth === parseInt(filterMonth);
        }
        return matchesSearch;
      })
    : [];

  const displayMonth = filterMonth && filterMonth.includes('-') 
    ? monthNames[statsMonth] 
    : (filterMonth ? monthNames[parseInt(filterMonth) - 1] : monthNames[currentMonth]);

  const handleStatsFilterChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      onFilterChange({ target: { value: "" } });
    } else {
      onFilterChange({ target: { value } });
    }
  };

  const isFutureMonth = (year, monthIndex) => {
    if (year > currentYear) return true;
    if (year === currentYear && monthIndex > currentMonth) return true;
    return false;
  };

  const renderMobileCards = () => {
    if (filteredRecords.length === 0) {
      return (
        <div className="mobile-empty-state">
          <div className="empty-icon"><FaCalendarAlt /></div>
          <p className="empty-text">
            {searchTerm || filterMonth ? "No matching records found" : "No attendance records yet"}
          </p>
          <p className="empty-subtext">
            {searchTerm || filterMonth ? "Try different search criteria" : "Start by checking in for today!"}
          </p>
        </div>
      );
    }

    return filteredRecords.map((record) => {
      const checkIn = formatDateTime(record.check_in);
      const checkOut = formatDateTime(record.check_out);
      const arrivalDev = getArrivalDeviation(record.check_in, record.expected_arrival);
      const departureDev = getDepartureDeviation(record.check_out, record.expected_leaving);
      const pauseMinutes = record.total_pause_minutes || 0;
      const hoursDisplay = getWorkingHoursDisplay(record.check_in, record.check_out, pauseMinutes);
      const status = record.status || "Present";

      let statusClass = "present";
      if (status.toLowerCase() === "absent") statusClass = "absent";
      else if (status.toLowerCase() === "late") statusClass = "late";
      else if (status.toLowerCase() === "leave") statusClass = "leave";

      let hoursBadgeClass = "good";
      const hoursNum = parseFloat(hoursDisplay) || 0;
      if (hoursNum < 4) hoursBadgeClass = "low";
      else if (hoursNum < 8) hoursBadgeClass = "half";
      else hoursBadgeClass = "good";

      return (
        <div className="attendance-card" key={record.id}>
          <div className="attendance-card-header">
            <span className="attendance-card-date">{checkIn.date}</span>
            <span className={`attendance-card-status ${statusClass}`}>{status}</span>
          </div>
          <div className="attendance-card-body">
            <div className="attendance-card-item">
              <span className="item-label">CHECK IN</span>
              <span className="item-value time-in">{checkIn.time !== "—" ? checkIn.time : "—"}</span>
              {arrivalDev && arrivalDev.type === "late" && (
                <span className="item-sub late-text">Late {formatDuration(arrivalDev.minutes)}</span>
              )}
              {arrivalDev && arrivalDev.type === "early" && (
                <span className="item-sub early-text">Early {formatDuration(arrivalDev.minutes)}</span>
              )}
            </div>
            <div className="attendance-card-item">
              <span className="item-label">CHECK OUT</span>
              <span className="item-value time-out">{checkOut.time !== "—" ? checkOut.time : "—"}</span>
              {departureDev && departureDev.type === "early" && (
                <span className="item-sub early-text">Early {formatDuration(departureDev.minutes)}</span>
              )}
              {departureDev && departureDev.type === "extra" && (
                <span className="item-sub extra-text">Extra {formatDuration(departureDev.minutes)}</span>
              )}
            </div>
            <div className="attendance-card-item">
              <span className="item-label">HOURS</span>
              <span className="item-value">{hoursDisplay !== "0m" && hoursDisplay !== "" ? hoursDisplay : "—"}</span>
              {pauseMinutes > 0 && (
                <span className="item-sub" style={{ fontSize: '8px', color: '#f59e0b' }}>
                  -{formatDuration(pauseMinutes)} break
                </span>
              )}
            </div>
          </div>
          <div className="attendance-card-footer">
            <div className="attendance-card-expected">
              <span>
                <span className="label">Arrival</span>
                <span className="value">{record.expected_arrival ? formatTime12Hour(record.expected_arrival) : "—"}</span>
              </span>
              <span>
                <span className="label">Leaving</span>
                <span className="value">{record.expected_leaving ? formatTime12Hour(record.expected_leaving) : "—"}</span>
              </span>
            </div>
            <div className="attendance-card-hours">
              <span className={`hours-badge-mobile ${hoursBadgeClass}`}>{hoursDisplay !== "0m" && hoursDisplay !== "" ? hoursDisplay : "—"}</span>
            </div>
          </div>
        </div>
      );
    });
  };

  // ==================== RENDER ====================
  return (
    <div className="students-card">
      {/* Monthly Summary Section */}
      <div className="monthly-summary">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <h3 className="summary-title" style={{ margin: 0 }}>
            Monthly Overview - {displayMonth} {statsYear}
            {hasData && (
              <span style={{ fontSize: "11px", color: "#888", marginLeft: "8px", fontWeight: "400" }}>
                (Off: {getOffDaysDescription(isSpecialIntern ? "intern_special" : employeeRole)}
                {totalHolidaysInMonth > 0 && ` | Holidays: ${totalHolidaysInMonth}`}
                {totalLeaveDaysInMonth > 0 && ` | Leave Days: ${totalLeaveDaysInMonth}`})
              </span>
            )}
          </h3>
          <select 
            className="form-input filter-select" 
            value={filterMonth} 
            onChange={handleStatsFilterChange}
            style={{ width: 'auto', minWidth: '160px' }}
          >
            <option value="">Current Month</option>
            {availableYears.map(year => 
              monthNames.map((m, i) => {
                const value = `${year}-${String(i + 1).padStart(2, '0')}`;
                const disabled = isFutureMonth(year, i);
                return (
                  <option key={value} value={value} disabled={disabled} style={disabled ? { color: '#cbd5e1' } : {}}>
                    {m} {year}{disabled ? ' (Future)' : ''}
                  </option>
                );
              })
            )}
          </select>
        </div>

        {hasData ? (
          isSpecialIntern ? (
            // ========== SPECIAL INTERN UI ==========
            <>
              {/* Rule Info Banner */}
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '12px',
                color: '#92400e',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <FaInfoCircle />
                <span>
                  <strong>Special Intern Rules:</strong> Sunday off • Work window 12:00 PM – 5:00 PM • Weekly minimum 15h • Flexible days
                </span>
              </div>

              {/* Row 1 */}
              <div className="summary-cards">
                <div className="summary-card">
                  <span className="summary-label">Total Expected Hours</span>
                  <span className="summary-value">{formatHoursMins(totalExpectedMonthMinutes)}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Actual Worked Hours</span>
                  <span className="summary-value" style={{ color: '#d97706' }}>
                    {formatHoursMins(specialTotalWorkedMinutes)}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Extra Hours</span>
                  <span className="summary-value" style={{ color: '#10b981' }}>
                    {formatHoursMins(specialExtraMinutes)}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Shortfall Hours</span>
                  <span className="summary-value" style={{ color: '#dc2626' }}>
                    {formatHoursMins(specialShortfallMinutes)}
                  </span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="summary-cards" style={{ marginTop: "12px" }}>
                <div className="summary-card">
                  <span className="summary-label">Working Days / Total</span>
                  <span className="summary-value">
                    <span style={{ color: "#d97706" }}>{workingDaysPassed - totalAbsentDays}</span> / {totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0)}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Absent Days</span>
                  <span className="summary-value" style={{ color: '#dc2626' }}>
                    {totalAbsentDays}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Leave Days</span>
                  <span className="summary-value" style={{ color: '#8b5cf6' }}>
                    {totalLeaveDaysInMonth || 0}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Weeks Tracked</span>
                  <span className="summary-value">{specialWeeklySummary.length}</span>
                </div>
              </div>

              {/* Weekly Breakdown */}
              <div className="summary-card" style={{
                marginTop: "12px",
                background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
                borderRadius: "12px",
                padding: "20px",
                border: "2px solid #e2e8f0"
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <span className="summary-label" style={{ color: '#475569', margin: 0, fontSize: '14px' }}>
                    📅 Weekly Breakdown (15h/week minimum)
                  </span>
                </div>

                {specialWeeklySummary && specialWeeklySummary.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {specialWeeklySummary.map((week, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          background: week.meetsMinimum ? '#ecfdf5' : '#fef2f2',
                          border: `1px solid ${week.meetsMinimum ? '#86efac' : '#fecaca'}`,
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px', minWidth: '130px' }}>
                          {week.weekLabel}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ fontSize: '13px' }}>
                            <span style={{ color: '#64748b', fontWeight: '500' }}>Worked: </span>
                            <span style={{
                              fontWeight: '800',
                              color: week.meetsMinimum ? '#059669' : '#dc2626',
                              fontSize: '15px'
                            }}>
                              {formatHoursMins(week.workedMinutes)}
                            </span>
                            <span style={{ color: '#94a3b8', fontSize: '12px', marginLeft: '4px' }}>
                              / {formatHoursMins(week.expectedMinutes)}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            <strong style={{ color: '#1e293b' }}>{week.daysWorked}</strong> day{week.daysWorked !== 1 ? 's' : ''} worked
                          </div>
                          {week.meetsMinimum ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '4px 10px', borderRadius: '12px',
                              background: '#10b981', color: 'white',
                              fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap'
                            }}>
                              ✅ {week.extraMinutes > 0 ? `+${formatHoursMins(week.extraMinutes)} extra` : 'Meets 15h'}
                            </span>
                          ) : (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '4px 10px', borderRadius: '12px',
                              background: '#dc2626', color: 'white',
                              fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap'
                            }}>
                              ⚠️ Short {formatHoursMins(week.shortfallMinutes)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>
                    No weeks tracked for this month
                  </div>
                )}
              </div>

              {/* Time Breakdown */}
              <div className="summary-card" style={{
                marginTop: "12px",
                background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
                borderRadius: "12px",
                padding: "20px",
                border: "2px solid #e2e8f0"
              }}>
                <span className="summary-label" style={{ marginBottom: "12px", textAlign: 'center', color: '#475569', display: 'block' }}>
                  ⏱️ Time Breakdown
                </span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {(() => {
                    const absentTime = specialAbsentMinutes || 0;
                    const leaveTime = specialLeaveMinutes || 0;
                    const shortfall = specialShortfallMinutes || 0;
                    const total = absentTime + leaveTime + shortfall;

                    return (
                      <>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#ef4444', fontFamily: 'monospace' }}>
                            {formatHoursMins(absentTime)}
                          </div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Absent</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#8b5cf6', fontFamily: 'monospace' }}>
                            {formatHoursMins(leaveTime)}
                          </div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Leave Days</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316', fontFamily: 'monospace' }}>
                            {formatHoursMins(shortfall)}
                          </div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Weekly Shortfall</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>=</span>
                        <div style={{
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                          borderRadius: '8px',
                          padding: '6px 14px',
                          border: '2px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '17px', fontWeight: '800', color: '#1e293b', fontFamily: 'monospace' }}>
                            {formatHoursMins(total)}
                          </div>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', fontWeight: '600' }}>Total</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            // ========== NORMAL EMPLOYEE UI ==========
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <span className="summary-label">Working Days in Month</span>
                  <span className="summary-value">
                    {totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0)} days
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Total Expected Hours</span>
                  <span className="summary-value">{formatHoursMins(totalExpectedMonthMinutes)}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Leave Days</span>
                  <span className="summary-value" style={{ color: '#8b5cf6' }}>
                    {totalLeaveDaysInMonth} days
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Daily Expected</span>
                  <span className="summary-value">{formatDuration(expectedDailyMinutes)}</span>
                </div>
              </div>

              <div className="summary-cards" style={{ marginTop: "12px" }}>
                <div className="summary-card">
                  <span className="summary-label">Days Worked / Total</span>
                  <span className="summary-value">
                    <span style={{ color: "#d97706" }}>{actualDaysWorked}</span> / {totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0)}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Actual Hours / Expected</span>
                  <span className="summary-value">
                    <span style={{ color: "#d97706" }}>{formatHoursMins(totalWorkedMinutes)}</span> / {formatHoursMins(totalExpectedMonthMinutes)}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Total Office Outside Time</span>
                  <span className="summary-value" style={{ color: totalPauseMinutesMonth > 0 ? '#f59e0b' : '#1e293b' }}>
                    {formatHoursMins(totalPauseMinutesMonth)}
                  </span>
                </div>
              </div>

              {/* Normal Time Breakdown — NO EXTRA */}
              <div className="summary-card-last" style={{ marginTop: "12px", width: "50%" }}>
                <span className="summary-label" style={{ marginBottom: "10px", textAlign: 'center', color: '#475569' }}>
                  Time Breakdown
                </span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {(() => {
                    const absentTime = absentDates.length * expectedDailyMinutes;
                    const leaveDaysTime = leaveDaysPassed * expectedDailyMinutes;
                    const lateTime = totalLateMinutes || 0;
                    const outsideTime = totalPauseMinutesMonth;
                    const earlyDepartureTime = totalEarlyDepartureMinutes || 0;
                    const total = Math.max(0, absentTime + leaveDaysTime + lateTime + outsideTime + earlyDepartureTime);

                    return (
                      <>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#ef4444', fontFamily: 'monospace' }}>{formatHoursMins(absentTime)}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Absent</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#8b5cf6', fontFamily: 'monospace' }}>{formatHoursMins(leaveDaysTime)}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Leave Days</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#f59e0b', fontFamily: 'monospace' }}>{formatHoursMins(lateTime)}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Late</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316', fontFamily: 'monospace' }}>{formatHoursMins(earlyDepartureTime)}</div>
                          <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Early Dep.</div>
                        </div>
                        <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>=</span>
                        <div style={{ textAlign: 'center', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '8px', padding: '6px 14px', border: '2px solid #e2e8f0' }}>
                          <div style={{ fontSize: '17px', fontWeight: '800', color: '#1e293b', fontFamily: 'monospace' }}>{formatHoursMins(total)}</div>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', fontWeight: '600' }}>Total</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )
        ) : (
          <div style={{ textAlign: "center", padding: "30px 20px" }}>
            <div style={{ fontSize: "40px", marginBottom: "8px" }}><FaCalendarAlt /></div>
            <p style={{ color: "#64748b", fontSize: "14px", fontWeight: "600", margin: "0 0 4px 0" }}>
              No attendance data available
            </p>
            <p style={{ color: "#94a3b8", fontSize: "12px", margin: 0 }}>
              No records found for {displayMonth} {statsYear}
            </p>
          </div>
        )}

        {/* ✅ Absent + Leave Dates (red + green) */}
        {hasData && (absentDates.length > 0 || leaveDatesList.length > 0) && (
          <div className="absent-dates-section" style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', border: '1px solid #fecaca', marginBottom: '20px', marginTop: '16px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#991b1b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span><FaExclamationTriangle /></span> Absent Dates ({absentDates.length} {absentDates.length === 1 ? 'day' : 'days'})
              {leaveDatesList.length > 0 && (
                <span style={{ marginLeft: '6px', color: '#059669', fontWeight: '600' }}>
                  + {leaveDatesList.length} leave {leaveDatesList.length === 1 ? 'day' : 'days'}
                </span>
              )}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }} className="absent-dates-grid">
              {absentDates.map((absentDay, index) => (
                <span key={`absent-${index}`} className="absent-date-badge" style={{ background: '#dc2626', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ opacity: 0.8 }}><FaCalendarAlt /></span>{absentDay.dayName} {absentDay.day}
                </span>
              ))}
              {leaveDatesList.map((leaveDay, index) => (
                <span key={`leave-${index}`} className="absent-date-badge" style={{ background: '#10b981', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ opacity: 0.8 }}><FaCalendarAlt /></span>{leaveDay.dayName} {leaveDay.day}
                </span>
              ))}
            </div>
          </div>
        )}

        {hasData && absentDates.length === 0 && leaveDatesList.length === 0 && workingDaysPassed > 0 && (
          <div className="absent-dates-section" style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '1px solid #bbf7d0', marginBottom: '20px', marginTop: '16px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#166534', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span><FaStar /></span> Perfect Attendance!
            </h4>
            <p style={{ color: '#166534', fontSize: '13px', margin: 0 }}>No absences so far this month. Keep it up!</p>
          </div>
        )}
      </div>

      <div className="card-header-custom">
        <div>
          <h3 className="card-title">My Attendance Records</h3>
          <p className="card-subtitle">Showing {filteredRecords.length} of {records ? records.length : 0} records</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <span className="search-icon"><FaSearch /></span>
            <input type="text" placeholder="Search by date..." className="search-input" value={searchTerm} onChange={onSearchChange} />
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="table-wrapper">
        <table className="students-table attendance-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Status</th>
              <th>Expected Arrival</th>
              <th>Expected Leaving</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record) => {
                const checkIn = formatDateTime(record.check_in);
                const checkOut = formatDateTime(record.check_out);
                const arrivalDev = getArrivalDeviation(record.check_in, record.expected_arrival);
                const departureDev = getDepartureDeviation(record.check_out, record.expected_leaving);
                const pauseMinutes = record.total_pause_minutes || 0;

                return (
                  <tr key={record.id}>
                    <td className="date-cell">{checkIn.date}</td>
                    <td className="time-cell">
                      <div className="time-cell-stack">
                        <span className="time-badge time-in">{checkIn.time}</span>
                        {arrivalDev && arrivalDev.type === "early" && (
                          <span className="status-badge status-early">Early {formatDuration(arrivalDev.minutes)}</span>
                        )}
                        {arrivalDev && arrivalDev.type === "late" && (
                          <span className="status-badge status-late">Late {formatDuration(arrivalDev.minutes)}</span>
                        )}
                      </div>
                    </td>
                    <td className="time-cell">
                      <div className="time-cell-stack">
                        <span className="time-badge time-out">{checkOut.time}</span>
                        {departureDev && departureDev.type === "early" && (
                          <span className="status-badge status-absent">Early {formatDuration(departureDev.minutes)}</span>
                        )}
                        {departureDev && departureDev.type === "extra" && (
                          <span className="status-badge status-extra">Extra {formatDuration(departureDev.minutes)}</span>
                        )}
                      </div>
                    </td>
                    <td className="hours-cell">
                      <span className="hours-badge">
                        {getWorkingHoursDisplay(record.check_in, record.check_out, pauseMinutes)}
                      </span>
                      {pauseMinutes > 0 && (
                        <span style={{ fontSize: '10px', color: '#f59e0b', display: 'block', marginTop: '3px', fontWeight: '500' }}>
                          -{formatDuration(pauseMinutes)} Outside Office
                        </span>
                      )}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge status-${record.status?.toLowerCase() || "present"}`}>
                        {record.status || "Present"}
                      </span>
                    </td>
                    <td className="time-cell">
                      <span className="time-badge time-in">{formatTime12Hour(record.expected_arrival)}</span>
                    </td>
                    <td className="time-cell">
                      <span className="time-badge time-out">{formatTime12Hour(record.expected_leaving)}</span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="empty-state">
                <td colSpan="7">
                  <div className="empty-state-content">
                    <div className="empty-icon"><FaCalendarAlt /></div>
                    <p className="empty-text">
                      {searchTerm || filterMonth ? "No matching records found" : "No attendance records yet"}
                    </p>
                    <p className="empty-subtext">
                      {searchTerm || filterMonth ? "Try different search criteria" : "Start by checking in for today!"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-attendance-cards">
        {renderMobileCards()}
      </div>
    </div>
  );
};

export default AttendanceTable;